import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {makeStyles} from '../../theme'
import {formatDate, formatInput, getTime, isSame, isValidDate, replaceFormat, setTime, toISO} from './utils'

export function useInputValue(value: string, inputDateFormat?: string, dateFormat?: string) {
  const outerValue = formatInput(value, inputDateFormat, dateFormat)

  const [inputValue, setInputValue] = useState(outerValue)
  const isValidInputFormat = useMemo(() => isValidDate(inputValue, inputDateFormat), [inputValue, inputDateFormat])

  // хедлим изенение данных кодом в обход пикера
  useEffect(() => {
    if (outerValue !== inputValue) {
      // console.log('INPUT FORCE CHANGED');
      setInputValue(outerValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outerValue])

  return [inputValue, setInputValue, isValidInputFormat] as const
}

export function useCalendarValue({value, dateFormat}: {value: string; dateFormat?: string}) {
  let outerValue = isValidDate(value, dateFormat)
    ? toISO(value, dateFormat)
    : isValidDate(value)
    ? toISO(value)
    : undefined

  if (outerValue) {
    // чтобы не реагировать на изменение времени
    outerValue = setTime(outerValue, '00:00')
  }

  const [calendarValue, setCalendarValue] = useState(outerValue)

  // хедлим изенение данных кодом в обход пикера
  useEffect(() => {
    if (outerValue !== calendarValue) {
      // console.log('CALENDAR FORCE CHANGED');
      setCalendarValue(outerValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outerValue])

  return [calendarValue, setCalendarValue] as const
}

type UpdaterParams = {
  newInputValue?: string
  newTimeValue?: string
}

export function useValueUpdater({
  value,
  inputValue,
  inputDateFormat,
  dateFormat,
  withTime,
  timeInputValue,
  setCalendarValue,
  onChange,
}: {
  value: string
  inputValue: string
  setCalendarValue: (value: string) => void
  inputDateFormat?: string
  dateFormat?: string
  withTime?: boolean
  timeInputValue?: string
  onChange?: (value: string) => void
}) {
  return useCallback(
    ({newInputValue = inputValue, newTimeValue = timeInputValue}: UpdaterParams) => {
      if (isValidDate(newInputValue, inputDateFormat)) {
        setCalendarValue(toISO(newInputValue, inputDateFormat))
        let finalDate = replaceFormat(newInputValue, inputDateFormat, dateFormat)
        if (withTime && newTimeValue) {
          finalDate = setTime(finalDate, newTimeValue, dateFormat, getTime(value))
        }

        if (!isSame(finalDate, value, withTime ? 'minute' : 'day')) {
          return onChange?.(finalDate)
        }
      }
    },
    [inputValue, inputDateFormat, dateFormat, onChange, timeInputValue, withTime, value, setCalendarValue],
  )
}

export function usePopup() {
  const inputRef = useRef<any>()
  const paperRef = useRef<any>()

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)

  useEffect(() => {
    if (inputRef.current) {
      setAnchorEl(inputRef.current)
    }
  }, [])

  return [inputRef, paperRef, anchorEl] as const
}

export function useTimeValue(value: string, withTime?: boolean) {
  const outerValue = getTime(value)

  const [timeInputValue, setTimeInputValue] = useState(outerValue)

  // хедлим изенение данных кодом в обход пикера
  useEffect(() => {
    if (withTime && outerValue !== timeInputValue) {
      setTimeInputValue(outerValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outerValue])

  return [timeInputValue, setTimeInputValue] as const
}

const useHelperStyles = makeStyles(() => ({
  root: {
    width: 'calc(100% + 63px)',
  },
}))

export function useHelperProps(withTime?: boolean) {
  const styles = useHelperStyles()

  return useMemo(
    () =>
      withTime
        ? {
            classes: styles,
          }
        : undefined,
    [withTime, styles],
  )
}

export function useHandlers({
  setInputValue,
  setTimeInputValue,
  setCalendarValue,
  onChange,
  updater,
  inputDateFormat,
}: {
  setInputValue: (value: string) => void
  setTimeInputValue: (value: string) => void
  setCalendarValue: (value: string | undefined) => void
  onChange?: (value: string) => void
  updater: (params: UpdaterParams) => void
  inputDateFormat?: string
}) {
  const onClear = useCallback(() => {
    setInputValue('')
    setTimeInputValue('')
    setCalendarValue(undefined)
    return onChange?.('')
  }, [onChange, setInputValue, setTimeInputValue, setCalendarValue])

  const onDateChange = useCallback(
    (date: string) => {
      const newInputValue = formatDate(date, inputDateFormat)
      setInputValue?.(newInputValue)
      updater({newInputValue})
    },
    [inputDateFormat, setInputValue, updater],
  )

  const onInputValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newInputValue = e.currentTarget.value
      setInputValue?.(newInputValue)
      updater({newInputValue})
    },
    [setInputValue, updater],
  )

  const onTimeInputValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newTimeValue = e.currentTarget.value
      setTimeInputValue?.(newTimeValue)
      updater({newTimeValue})
    },
    [setTimeInputValue, updater],
  )

  return [onClear, onDateChange, onInputValueChange, onTimeInputValueChange] as const
}
